import React from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  Hidden
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Monitor as MonitorIcon,
  Delete as DeleteIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ServerTableRow = ({
  server,
  isSelected,
  isDeleting,
  hasPendingOperation,
  onSelect,
  onDelete,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => {
    if (server.isRegistering || isDeleting) return;
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMonitor = () => {
    if (server.isRegistering || server.isRegistrationFailed) return;
    handleMenuClose();
    navigate(`/monitoring/${server.nodeId || server.id}`);
  };

  const handleDelete = async () => {
    if (server.isRegistering || isDeleting) return;
    handleMenuClose();
    const confirmMessage = server.isRegistrationFailed 
      ? `Are you sure you want to delete the failed node "${server.hostname}"?`
      : `Are you sure you want to delete node "${server.hostname}"?`;
    
    if (onDelete && window.confirm(confirmMessage)) {
      await onDelete(server.id);
      if (onRefresh) {
        await onRefresh();
      }
    }
  };

  const getStatusChipProps = (status, isRegistering, isRegistrationFailed) => {
    if (isRegistering) {
      return {
        label: status,
        color: 'warning',
        icon: <CircularProgress size={12} sx={{ color: 'inherit' }} />
      };
    }
    if (isRegistrationFailed) {
      return {
        label: status,
        color: 'error',
        icon: <ErrorIcon sx={{ fontSize: '12px' }} />
      };
    }
    return {
      label: status,
      color: status === 'Active' ? 'success' : 'error'
    };
  };

  const getRowOpacity = () => {
    if (isDeleting) return 0.5;
    if (server.isRegistering) return 0.7;
    if (server.isRegistrationFailed) return 0.8;
    return 1;
  };

  return (
    <TableRow
      hover={!server.isRegistering && !isDeleting}
      selected={isSelected}
      sx={{
        opacity: getRowOpacity(),
        '&.Mui-selected': {
          backgroundColor: 'rgba(25, 118, 210, 0.12)',
        },
        '&:hover': {
          backgroundColor: (server.isRegistering || isDeleting) ? 'inherit' : 'rgba(255, 255, 255, 0.04)',
        }
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(server.id)}
          disabled={server.isRegistering || isDeleting}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            {...getStatusChipProps(server.status, server.isRegistering, server.isRegistrationFailed)}
            size="small"
            sx={{ minWidth: 80 }}
          />
          {isDeleting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} sx={{ color: 'error.main' }} />
              <Typography variant="caption" color="error.main">
                Deleting
              </Typography>
            </Box>
          )}
          {server.isRegistering && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HourglassEmptyIcon sx={{ fontSize: '0.875rem', color: 'warning.main' }} />
              <Typography variant="caption" color="warning.main">
                Setting up
              </Typography>
            </Box>
          )}
          {server.isRegistrationFailed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ErrorIcon sx={{ fontSize: '0.875rem', color: 'error.main' }} />
              <Typography variant="caption" color="error.main">
                Setup failed
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {server.hostname}
        </Typography>
      </TableCell>
      <TableCell>{server.region}</TableCell>
      <Hidden smDown>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {server.machineId}
          </Typography>
        </TableCell>
      </Hidden>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {server.ipAddress}
        </Typography>
      </TableCell>
      <Hidden smDown>
        <TableCell>{server.port}</TableCell>
      </Hidden>
      <TableCell>{server.type}</TableCell>
      <Hidden mdDown>
        <TableCell>
          {server.isRegistering ? 'Detecting...' : 
           server.isRegistrationFailed ? 'Unknown' : server.os}
        </TableCell>
        <TableCell>
          {server.isRegistering || server.isRegistrationFailed ? 'N/A' : (server.gpuType || 'N/A')}
        </TableCell>
        <TableCell>
          {server.isRegistering || server.isRegistrationFailed ? 'N/A' : (server.gpu || 'None')}
        </TableCell>
        <TableCell>
          {server.isRegistering || server.isRegistrationFailed ? 'N/A' : (server.numGpus || 0)}
        </TableCell>
        <TableCell>
          {server.isRegistering || server.isRegistrationFailed ? 'N/A' : `${server.vram || 0} GB`}
        </TableCell>
        <TableCell>
          {server.isRegistering ? 'Setting up...' : 
           server.isRegistrationFailed ? 'Setup failed' : server.function}
        </TableCell>
        <TableCell>
          <Chip
            label={server.isRegistering ? 'Setting up' : 
                   server.isRegistrationFailed ? 'Failed' : server.health}
            color={
              server.isRegistering ? 'warning' :
              server.isRegistrationFailed ? 'error' :
              server.health === 'Good' ? 'success' :
              server.health === 'Moderate' ? 'warning' :
              'error'
            }
            size="small"
          />
        </TableCell>
      </Hidden>
      <TableCell align="right">
        <IconButton
          onClick={handleMenuOpen}
          disabled={server.isRegistering || isDeleting}
          size="small"
          sx={{
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem 
            onClick={handleMonitor}
            disabled={server.isRegistrationFailed}
            sx={{
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <MonitorIcon sx={{ mr: 1 }} />
            Monitor
          </MenuItem>
          <MenuItem 
            onClick={handleDelete} 
            sx={{ 
              color: server.isRegistrationFailed ? 'error.main' : 'error.main'
            }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default ServerTableRow; 